import { ELEMENT_TYPE, QR_VARIABLE_KEY } from "./schema";
import {
  ASSET_SELECT_TYPES,
  createDefaultVariableAttrs,
  normalizeTemplateVariables,
  syncVariableKeyFields,
} from "../../CertificateOnboarding/utils/variables";

export const VARIABLE_PATTERN_SOURCE =
  "\\{\\{\\s*([a-zA-Z][a-zA-Z0-9_-]*)\\s*\\}\\}";

export const createVariableRegex = () =>
  new RegExp(VARIABLE_PATTERN_SOURCE, "g");

export function extractKeysFromString(input) {
  const keys = [];
  const regex = createVariableRegex();
  let match = regex.exec(String(input ?? ""));
  while (match) {
    if (!keys.includes(match[1])) keys.push(match[1]);
    match = regex.exec(String(input ?? ""));
  }
  return keys;
}

/**
 * Every variable key used by the design with where it is used.
 * @returns {{ key: string, usage: "text"|"image"|"qr", elementIds: string[] }[]}
 */
export function extractDesignVariables(elements = []) {
  const byKey = new Map();
  const add = (key, usage, elementId) => {
    const entry = byKey.get(key) || { key, usage, elementIds: [] };
    if (!entry.elementIds.includes(elementId)) entry.elementIds.push(elementId);
    byKey.set(key, entry);
  };

  elements.forEach((element) => {
    if (element.type === ELEMENT_TYPE.TEXT) {
      extractKeysFromString(element.content).forEach((key) =>
        add(key, "text", element.id),
      );
    } else if (element.type === ELEMENT_TYPE.IMAGE && element.variableKey) {
      add(element.variableKey, "image", element.id);
    } else if (element.type === ELEMENT_TYPE.QR) {
      add(element.variableKey || QR_VARIABLE_KEY, "qr", element.id);
    }
  });

  return [...byKey.values()];
}

/** Signature slots are keyed signatureImage1..n, the format the backend expects. */
const SIGNATURE_KEY = /^signatureImage(\d+)$/;
const LEGACY_SIGNATURE_KEY = /^signature(\d*)$/;

export const signatureImageKey = (number) => `signatureImage${number}`;

export function formatVariableLabel(key) {
  const signature = SIGNATURE_KEY.exec(String(key));
  if (signature) return `Signature ${signature[1]}`;
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function inferDataType(key, usage) {
  if (usage === "qr" || key === QR_VARIABLE_KEY) return "qr-code";
  const lower = key.toLowerCase();
  if (usage === "image") {
    if (
      lower.includes("signature") ||
      lower.includes("footer") ||
      lower.includes("photo") ||
      lower.includes("avatar") ||
      lower.includes("picture")
    )
      return "photo";
    return "logo";
  }
  if (
    lower.includes("date") ||
    /On$/.test(key) ||
    /^valid(Until|From|Till)/i.test(key)
  )
    return "date";
  if (lower.includes("email")) return "email";
  if (lower.includes("phone") || lower.includes("mobile")) return "phone";
  return "textField";
}

/** Issuer-level values (signatory, org name) default to the template form. */
export function inferFormType(key, usage) {
  if (usage === "image" && !key.toLowerCase().includes("photo"))
    return "template";
  const lower = key.toLowerCase();
  const templateHints = [
    "issuer",
    "organization",
    "organisation",
    "signatory",
    "director",
    "company",
    "institution",
  ];
  return templateHints.some((hint) => lower.includes(hint))
    ? "template"
    : "subject";
}

export function createVariableAttrs(key, usage) {
  const attrs = syncVariableKeyFields(createDefaultVariableAttrs(key), key);
  return {
    ...attrs,
    "data-label": formatVariableLabel(key),
    "data-type": inferDataType(key, usage),
    "data-form-type": inferFormType(key, usage),
    "data-required": usage !== "image",
    "max-characters": usage === "text" ? 80 : attrs["max-characters"],
    ...(usage === "image" ? { "max-size": "2500kb" } : {}),
  };
}

function withUploadDataType(attrs, key, usage) {
  if (usage !== "image" || ASSET_SELECT_TYPES.includes(attrs["data-type"])) {
    return attrs;
  }
  return { ...attrs, "data-type": inferDataType(key, usage) };
}

export function renameLegacySignatureKeys(elements = [], variables = {}) {
  const used = extractDesignVariables(elements);
  const taken = new Set(used.map((entry) => entry.key));
  const textKeys = new Set();
  elements.forEach((element) => {
    if (element.type === ELEMENT_TYPE.TEXT) {
      extractKeysFromString(element.content).forEach((key) =>
        textKeys.add(key),
      );
    }
  });

  const renames = new Map();
  used.forEach(({ key, usage }) => {
    const match = LEGACY_SIGNATURE_KEY.exec(key);
    if (usage !== "image" || !match || textKeys.has(key)) return;
    const target = signatureImageKey(match[1] || 1);
    if (taken.has(target) || variables[target]) return;
    taken.add(target);
    renames.set(key, target);
  });
  if (!renames.size) return { elements, variables };

  const nextElements = elements.map((element) => {
    const target =
      element.type === ELEMENT_TYPE.IMAGE && renames.get(element.variableKey);
    if (!target) return element;
    return {
      ...element,
      variableKey: target,
      name: element.name === element.variableKey ? target : element.name,
    };
  });

  const nextVariables = {};
  Object.entries(variables).forEach(([key, attrs]) => {
    const target = renames.get(key);
    if (!target) {
      nextVariables[key] = attrs;
      return;
    }
    const source = attrs || {};
    const renamed = syncVariableKeyFields(source, target);
    if (renamed["data-label"] !== source["data-label"]) {
      renamed["data-label"] = formatVariableLabel(target);
    }
    nextVariables[target] = renamed;
  });

  return { elements: nextElements, variables: nextVariables };
}

export function syncDesignVariables(variables = {}, elements = []) {
  const used = extractDesignVariables(elements);
  const next = {};
  used.forEach(({ key, usage }) => {
    next[key] = variables[key]
      ? withUploadDataType({ ...variables[key] }, key, usage)
      : createVariableAttrs(key, usage);
  });
  const orphaned = Object.keys(variables).filter((key) => !next[key]);
  return { variables: normalizeTemplateVariables(next), orphaned, used };
}

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export function resolveTemplateString(
  input,
  values = {},
  { escape = false, missing = "token" } = {},
) {
  return String(input ?? "").replace(createVariableRegex(), (token, key) => {
    const value = values[key];
    if (value === undefined || value === null || value === "") {
      return missing === "empty" ? "" : token;
    }
    return escape ? escapeHtml(value) : String(value);
  });
}

export { escapeHtml };

export const SAMPLE_VALUES = {
  recipientName: "Aarav Sharma",
  fullName: "Aarav Sharma",
  name: "Aarav Sharma",
  courseName: "Advanced Data Analytics",
  programName: "Leadership Development Programme",
  eventName: "National Innovation Summit 2026",
  issueDate: new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }),
  validUntil: "31 December 2027",
  issuerName: "Dr. Meera Iyer",
  issuerRole: "Director of Programmes",
  // Explicit, because the "contains name" fallback below would otherwise show
  // the recipient's name as the person signing.
  signatoryName: "Dr. Meera Iyer",
  signatoryTitle: "Director of Programmes",
  signatoryName2: "Prof. Rajan Mehta",
  signatoryTitle2: "Registrar",
  organizationName: "CredIssuer Academy",
  certificateId: "CI-2026-004815",
  credentialId: "CI-2026-004815",
  grade: "Distinction",
  score: "92%",
  employeeId: "EMP-10482",
  department: "Engineering",
  designation: "Senior Analyst",
  rollNumber: "22CS1043",
  className: "Grade 10 — Section B",
  schoolName: "Delhi Public School",
  duration: "12 weeks",
  hours: "48 hours",
  bloodGroup: "O+",
  academicYear: "2026–27",
  memberId: "MEM 2048 1731",
  membershipTier: "Gold",
  memberSince: "2021",
  companyName: "Nimbus Labs",
  badgeRole: "Speaker",
  eventDates: "12–14 March 2026",
  eventVenue: "Bengaluru International Centre",
  dateOfJoining: "14 July 2022",
  dateOfBirth: "12 May 2008",
  phone: "+91 98765 43210",
  emergencyContact: "+91 98450 12345",
  workLocation: "Bengaluru HQ",
  guardianName: "Rohan Sharma",
  membershipPlan: "Annual Family",
  branch: "Indiranagar",
  ticketNumber: "NIS26-08814",
  accessZone: "All Access",
  track: "Product & Design",
  lockerNumber: "L-214",
};

export const LONG_SAMPLE_VALUES = {
  recipientName: "Venkata Subrahmanya Lakshminarasimha Raghunathan",
  fullName: "Venkata Subrahmanya Lakshminarasimha Raghunathan",
  name: "Venkata Subrahmanya Lakshminarasimha Raghunathan",
  courseName:
    "Professional Certificate in Applied Machine Learning and Responsible AI Systems",
  programName:
    "Executive Leadership and Strategic Transformation Programme for Public Institutions",
  eventName:
    "International Conference on Sustainable Infrastructure and Climate Resilience 2026",
  organizationName:
    "National Institute of Skill Development and Entrepreneurship",
  issuerName: "Prof. Dr. Anantharaman Krishnamurthy",
  issuerRole: "Vice Chancellor and Chief Academic Officer",
  department: "Research, Innovation and International Partnerships",
  designation: "Principal Consultant — Digital Public Infrastructure",
};

export function sampleValueFor(key, { long = false } = {}) {
  if (long && LONG_SAMPLE_VALUES[key]) return LONG_SAMPLE_VALUES[key];
  if (SAMPLE_VALUES[key]) return SAMPLE_VALUES[key];
  const lower = key.toLowerCase();
  if (lower.includes("date")) return SAMPLE_VALUES.issueDate;
  if (lower.includes("name"))
    return long
      ? LONG_SAMPLE_VALUES.recipientName
      : SAMPLE_VALUES.recipientName;
  return formatVariableLabel(key);
}

export function buildSampleValues(keys = [], options = {}) {
  return keys.reduce((acc, key) => {
    acc[key] = sampleValueFor(key, options);
    return acc;
  }, {});
}
